import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { TagsWithPopover } from "kontrolEdge/components/Shared/TagsWithPopover/TagsWithPopover";

export interface SupportTicket {
	id: string | number;
	type: string;
	subject: string;
	priority: number;
	reporterEmail: string;
	reporter: string;
	service_id: string | string[];
	updatedAt: string;
	status: {
		label: string;
		label_for_customers?: string;
		name?: string;
	};
	customer?: {
		name: string;
		id: number;
		chargebeeCustomerId: string
	};
	key?: string | number;
}


export const getStatusText = (s: SupportTicket["status"]) =>
	withFallback(typeof s === "string" ? s : s?.label || s?.name);

// A function that returns the columns based on the global product value.
export const getTicketColumns = (
	globalProduct: string|null
): GenericTableColumn<SupportTicket>[] => [
	{
		header: "Type",
		renderCell: (row) => withFallback(row.type),
		sortable: true,
		sortAccessor: (row) => row.type ?? "",
		width: "10%",
		wrapMode: "nowrap",
	},
	{
		header: "Ticket N°",
		renderCell: (row) =>
			withFallback(globalProduct === "CTX Reflector" ? row.id : row.key),
		width: "7%",
		wrapMode: "nowrap",
	},
	{
		header: "Title",
		renderCell: (row) => withFallback(row.subject),
		width: "auto",
		wrapMode: "clamp-2"
	},
	{
		header: "Reporter",
		renderCell: (row) =>
			row.reporter || row.reporterEmail ? (
			<div className="flex flex-col whitespace-normal">
			<span className="font-medium text-sm">{withFallback(row.reporter)}</span>
			<span className="text-xs text-gray-500 truncate">{withFallback(row.reporterEmail)}</span>
			</div>
			) : (
			"—"
			),
		width: "18%",
		wrapMode: "wrap",
	},
	// Service ID column - only show for Kontrol Edge
	...(globalProduct === "Kontrol Edge" ? [{
		header: "Service ID",
		renderCell: (row: SupportTicket) => {
			// Handle service_id as string, array, or null/undefined
			if (!row.service_id) {
				return <span className="text-semantic-text-secondary">-</span>;
			}
			// Convert to array for TagsWithPopover
			let serviceIds: string[] = [];
			if (Array.isArray(row.service_id)) {
				// Already an array
				serviceIds = row.service_id;
			} else if (typeof row.service_id === "string") {
				if (row.service_id.includes(",")) {
					// Split by comma, trim each part, and filter out empty strings
					serviceIds = row.service_id
						.split(",")
						.map(id => id.trim())
						.filter(id => id.length > 0);
				} else {
					serviceIds = [row.service_id];
				}
			}
			return <TagsWithPopover tags={serviceIds} maxVisible={1} copyOnClick={true} />;
		},
		width: "14%",
		wrapMode: "nowrap" as const,
	}] : []),
	{
		header: "Status",
		renderCell: (row) => getStatusText(row.status),
		sortable: true,
		sortAccessor: (row) =>
			typeof row.status === "string" ? row.status || "" : (row.status?.label || row.status?.name || ""),
		width: "12%",
		wrapMode: "nowrap" as const,
	},
	{
		header: "Priority",
		renderCell: (row) => withFallback(row.priority),
		sortable: true,
		sortAccessor: (row) => Number(row.priority),
		width: "8%",
		wrapMode: "nowrap" as const,
	},
	{
		header: "Last Update",
		renderCell: (row) => {
			if (!row.updatedAt) return "—";
			// Extract date part from ISO string (e.g., "2026-02-16T18:05:13.214-0600" -> "2026-02-16")
			const datePart = row.updatedAt.split("T")[0];
			return datePart || "—";
		},
		sortable: true,
		sortAccessor: (row) => {
			if (!row.updatedAt) return 0;
			// Extract date part and convert to timestamp for sorting
			const datePart = row.updatedAt.split("T")[0];
			if (!datePart) return 0;
			return new Date(datePart).getTime();
		},
		width: "8%",
		wrapMode: "nowrap" as const,
	}
];
